import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, AlertTriangle, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function KnifeTicks({ knives, present, toggle }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 py-2" data-testid="knife-ticks">
      {knives.map((k) => {
        const on = present.includes(k);
        return (
          <button key={k} onClick={() => toggle(k)} data-testid={`knife-tick-${k}`} aria-pressed={on}
            className={`h-14 rounded-lg border-2 font-mono font-bold text-base flex items-center justify-center gap-1.5 transition-colors ${on ? "border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30" : "border-border bg-white dark:bg-stone-900 text-stone-500"}`}>
            {on && <Check className="h-4 w-4" />} {k}
          </button>
        );
      })}
    </div>
  );
}

function Stepper({ counted, setCounted, bad }) {
  const n = Number(counted) || 0;
  return (
    <div className="flex items-center justify-center gap-3 py-2">
      <Button variant="outline" className="h-16 w-16" onClick={() => setCounted(String(Math.max(0, n - 1)))} data-testid="knife-count-minus"><Minus className="h-6 w-6" /></Button>
      <Input type="number" inputMode="numeric" min="0" value={counted} onChange={(e) => setCounted(e.target.value)}
        className={`h-16 w-28 text-center text-3xl font-display font-bold tnum ${bad ? "border-rose-500 text-rose-700" : ""}`} data-testid="knife-count-input" />
      <Button variant="outline" className="h-16 w-16" onClick={() => setCounted(String(n + 1))} data-testid="knife-count-plus"><Plus className="h-6 w-6" /></Button>
    </div>
  );
}

export default function KnifeCountDialog({ target, onClose, onSaved }) {
  const [counted, setCounted] = useState("");
  const [present, setPresent] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCounted(target ? String(target.area.expected) : "");
    setPresent([]);
    setNote("");
  }, [target]);

  if (!target) return null;
  const { area, shift } = target;
  const numbered = area.knives?.length > 0;
  const n = numbered ? present.length : Number(counted);
  const valid = numbered || (counted !== "" && Number.isInteger(n) && n >= 0);
  const missing = numbered ? area.knives.filter((k) => !present.includes(k)) : [];
  const mismatch = valid && n !== area.expected;
  const toggle = (k) => setPresent((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const save = async () => {
    if (!valid) return toast.error("Enter the number of knives counted");
    if (mismatch && !note.trim()) return toast.error("Add a note about the missing or extra knife");
    setBusy(true);
    try {
      await api.post("/knives/counts", numbered
        ? { location_id: area.location_id, shift, present, note }
        : { location_id: area.location_id, shift, counted: n, note });
      toast[mismatch ? "warning" : "success"](mismatch ? `${area.name}: count mismatch recorded` : `${area.name}: ${n} knives, all present`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const diff = area.expected - n;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="knife-count-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{area.name} · {shift === "start" ? "Start" : "End"} of shift</DialogTitle>
          <DialogDescription>
            {numbered ? `Tap each knife you can see (${present.length} of ${area.knives.length} ticked).` : `Expected ${area.expected} knives. Count every knife in this area.`}
          </DialogDescription>
        </DialogHeader>
        {numbered ? <KnifeTicks knives={area.knives} present={present} toggle={toggle} /> : <Stepper counted={counted} setCounted={setCounted} bad={mismatch} />}
        {mismatch && (
          <div className="space-y-2" data-testid="knife-count-mismatch">
            <div className="flex gap-2 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                {numbered ? <>Missing: <b className="font-mono">{missing.join(", ")}</b>.</> : diff > 0 ? `${diff} knife${diff !== 1 ? "s" : ""} missing.` : `${-diff} extra knife${diff !== -1 ? "s" : ""}.`}{" "}
                Stop and search the area. A supervisor must sign this off.
              </div>
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Where it was last seen, action taken (required)" data-testid="knife-count-note" />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="knife-count-cancel">Cancel</Button>
          <Button className={`h-12 px-6 ${mismatch ? "bg-rose-600 hover:bg-rose-700" : ""}`} onClick={save} disabled={busy} data-testid="knife-count-save">
            {busy ? "Saving…" : mismatch ? "Record mismatch" : "Confirm count"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
